import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type RefObject,
} from "react"

import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { usePageShortcuts } from "@/hooks/usePageShortcuts"

interface UseSearchShortcutOptions {
  enabled?: boolean
  inputRef: RefObject<HTMLInputElement | null>
  label?: string
  onClear?: () => void
  value?: string
}

export function useSearchShortcut({
  enabled = true,
  inputRef,
  label = "Search this page",
  onClear,
  value,
}: UseSearchShortcutOptions) {
  const [isFocused, setIsFocused] = useState(false)
  const [uncontrolledValue, setUncontrolledValue] = useState("")
  const normalizedValue = value ?? uncontrolledValue

  const handleFocusSearch = useCallback(() => {
    const input = inputRef.current
    if (!input) {
      return
    }

    input.focus()
    input.select()
  }, [inputRef])

  useEffect(() => {
    const input = inputRef.current
    if (!input) {
      return
    }

    input.setAttribute("data-keyboard-search-input", "true")

    const handleFocus = () => setIsFocused(true)
    const handleBlur = () => setIsFocused(false)
    const handleInput = () => {
      if (value === undefined) {
        setUncontrolledValue(input.value)
      }
    }

    if (value === undefined) {
      setUncontrolledValue(input.value)
    }

    input.addEventListener("focus", handleFocus)
    input.addEventListener("blur", handleBlur)
    input.addEventListener("input", handleInput)
    input.addEventListener("change", handleInput)

    return () => {
      input.removeEventListener("focus", handleFocus)
      input.removeEventListener("blur", handleBlur)
      input.removeEventListener("input", handleInput)
      input.removeEventListener("change", handleInput)
      input.removeAttribute("data-keyboard-search-input")
    }
  }, [inputRef, value])

  useEscapeShortcut({
    allowInInputs: true,
    description: "Clear search",
    enabled: enabled && isFocused && !!onClear && normalizedValue.length > 0,
    handler: () => {
      if (value === undefined) {
        setUncontrolledValue("")
      }
      onClear?.()
    },
    priority: ESCAPE_SHORTCUT_PRIORITY.ACTIVE_ITEM_CLEAR,
  })

  const shortcuts = useMemo(
    () => [
      {
        action: handleFocusSearch,
        command: {
          kind: "search" as const,
          scope: "page" as const,
        },
        enabled,
        key: "/",
        label,
      },
    ],
    [enabled, handleFocusSearch, label],
  )

  usePageShortcuts(shortcuts, enabled)
}
